import React, { useEffect } from 'react';

import { useAuth } from './AuthContext';
import { usesSso } from './authConfig';

/**
 * EAA-1694: gate for the editor's AUTHORING routes only.
 *
 * Deliberately not applied to `/embed/*`, `/h5p/*` or `/play/*`: those are opened by a host
 * LMS inside an iframe and authenticate with a content-session JWT or a public key. They
 * never had an interactive user session, and bouncing them to a login page would break
 * every existing embed.
 *
 * In `token` mode this renders its children unconditionally — the editor's historical
 * pasted-API-token flow is untouched by this ticket.
 */
export const RequireSso: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { status, login, memberships, claims, logout } = useAuth();

    useEffect(() => {
        if (usesSso && status === 'anonymous') {
            login();
        }
    }, [status, login]);

    if (!usesSso) {
        return <>{children}</>;
    }

    if (status === 'loading') {
        return <div className="p-8 text-sm text-muted-foreground">Đang kiểm tra đăng nhập…</div>;
    }

    if (status === 'anonymous') {
        // The effect above is already redirecting to the portal; render a holding state
        // rather than the editor, so no authenticated-only UI flashes first.
        return <div className="p-8 text-sm text-muted-foreground">Đang chuyển tới trang đăng nhập…</div>;
    }

    if (status === 'no-access') {
        // Authenticated, but granted no tenant. "Sign in" is not the fix and offering it
        // would loop the user straight back here.
        return (
            <div className="p-8 space-y-3">
                <h1 className="text-lg font-semibold">Chưa được cấp quyền truy cập</h1>
                <p className="text-sm text-muted-foreground">
                    Tài khoản {claims?.email || claims?.sub} đã đăng nhập thành công nhưng chưa được
                    gán vào cơ sở nào. Vui lòng liên hệ quản trị viên của đơn vị để được cấp quyền.
                </p>
                <button type="button" className="text-sm underline" onClick={logout}>
                    Đăng xuất
                </button>
            </div>
        );
    }

    if (memberships.length === 0) {
        return null;
    }

    return <>{children}</>;
};
